import { supabase } from './api'

/**
 * Outbound cascading messages — what the shop tells another team (0025).
 *
 * An admin writes one, usually at the Shop L10 Conclude, and ticks the teams it
 * goes to. The weekday 5:53pm Ninety sync posts each team's copy as a Ninety
 * Cascading Message and stamps it sent. The Ninety connector cannot edit or
 * delete a message once posted, so the database refuses edits as soon as any
 * team has it (`locked`).
 *
 * Inbound messages (Leadership → shop) are not here: they are headlines with
 * `cascaded_from` set, in headlinesApi.ts.
 */

/**
 * The teams the shop may send to. Mirrors the CHECK on
 * cascade_out_targets.team in 0025, which is the real control — keep the two
 * in step, the way HEADLINE_MAX mirrors create_headline.
 */
export const CASCADE_TEAMS = ['Leadership Team', 'Sales'] as const

/** Matches the cap enforced by admin_create_cascade / admin_update_cascade. */
export const CASCADE_MAX = 280

export interface CascadeTarget {
  team: string
  /** When the sync posted it to that team in Ninety. Null = still to send. */
  sent_at: string | null
}

export interface CascadeOut {
  id: string
  body: string
  details: string | null
  created_by_id: string | null
  created_by_name: string
  mine: boolean
  /** Set when it was written in a meeting's Conclude section. */
  meeting_id: string | null
  created_at: string
  archived: boolean
  targets: CascadeTarget[]
  /** True once any team has it — it can no longer be edited. */
  locked: boolean
}

export interface CascadeDraft {
  body: string
  details: string
  teams: string[]
}

function fail(error: { code?: string; message?: string }): Error {
  if (error.code === '28000') {
    return new Error('This link is not recognised. Ask Scott for a new one.')
  }
  if (error.code === '42501') {
    return new Error(error.message || 'You do not have permission to do that.')
  }
  if (error.code === '22023') {
    return new Error(error.message || 'That value is not allowed.')
  }
  return new Error(error.message || 'Something went wrong. Try again.')
}

export async function listCascadesOut(
  token: string,
  includeArchived = false,
): Promise<CascadeOut[]> {
  const { data, error } = await supabase.rpc('list_cascades_out', {
    p_token: token,
    p_include_archived: includeArchived,
  })
  if (error) throw fail(error)
  return (data as CascadeOut[] | null) ?? []
}

export async function adminCreateCascade(
  token: string,
  draft: CascadeDraft,
  meetingId: string | null = null,
): Promise<string> {
  const { data, error } = await supabase.rpc('admin_create_cascade', {
    p_token: token,
    p_body: draft.body,
    p_details: draft.details,
    p_teams: draft.teams,
    p_meeting_id: meetingId,
  })
  if (error) throw fail(error)
  return data as string
}

/** Refused by the database once any team has it. */
export async function adminUpdateCascade(
  token: string,
  cascadeId: string,
  draft: CascadeDraft,
): Promise<void> {
  const { error } = await supabase.rpc('admin_update_cascade', {
    p_token: token,
    p_cascade_id: cascadeId,
    p_body: draft.body,
    p_details: draft.details,
    p_teams: draft.teams,
  })
  if (error) throw fail(error)
}

/** Archiving one that has not been sent yet cancels it. */
export async function adminArchiveCascade(
  token: string,
  cascadeId: string,
  archived: boolean,
): Promise<void> {
  const { error } = await supabase.rpc('admin_archive_cascade', {
    p_token: token,
    p_cascade_id: cascadeId,
    p_archived: archived,
  })
  if (error) throw fail(error)
}

/** Permanent. Any copy already in Ninety stays there. */
export async function adminDeleteCascade(token: string, cascadeId: string): Promise<void> {
  const { error } = await supabase.rpc('admin_delete_cascade', {
    p_token: token,
    p_cascade_id: cascadeId,
  })
  if (error) throw fail(error)
}
